import * as signalR from '@microsoft/signalr'
import { flowHubService } from './flowHub.service'
import { tokenStorage } from './tokenStorage'
import { setRealtimeConnectionId } from './realtimeIdentity'
import { attachSettingsSync } from './settingsSync'

/**
 * The token to hand the hub, read at the moment it is asked for.
 *
 * `accessTokenFactory` is called again on every reconnect, which is the whole point of it being a
 * function — and every hub here was closing over the token it was started with, so a reconnect
 * re-presented a token that had since expired and the connection never came back. Live updates
 * would just stop, quietly, and stay stopped until the page was reloaded.
 *
 * Access tokens are short now and refreshed underneath, so the captured one is stale within the
 * hour rather than within the day. Reading storage each time is what makes reconnects survive
 * that. The value passed at start is kept as the fallback for the first connect, before anything
 * has been written to storage.
 */
const currentToken = (fallback: string) => () => tokenStorage.getAccessToken() || fallback

// Empty means same-origin, which is what dev uses: vite proxies /hub through to the API so the
// hubs work without a second host. Production sets this to the API origin.
const SIGNALR_URL = import.meta.env.VITE_SIGNALR_URL || ''

class SignalRService {
  private chatConnection: signalR.HubConnection | null = null
  private chatConnectionPromise: Promise<signalR.HubConnection> | null = null
  private notificationConnection: signalR.HubConnection | null = null

  // Chat Hub
  async startChatConnection(token: string) {
    if (this.chatConnection?.state === signalR.HubConnectionState.Connected) {
      return this.chatConnection
    }

    // Return existing promise if connection is being established (prevents double connections)
    if (this.chatConnectionPromise) {
      return this.chatConnectionPromise
    }

    // Use Trace for most detailed SignalR logs, Debug for detailed, Information for normal
    const logLevel = import.meta.env.DEV ? signalR.LogLevel.Trace : signalR.LogLevel.Information

    this.chatConnectionPromise = (async () => {
      this.chatConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${SIGNALR_URL}/hub/chat`, {
          accessTokenFactory: currentToken(token),
        })
        .withAutomaticReconnect()
        .configureLogging(logLevel)
        .build()

      // Add connection state handlers for chat
      this.chatConnection.onreconnecting(error => {
        console.warn('⚠️ SignalR Chat reconnecting...', error)
      })

      this.chatConnection.onreconnected(connectionId => {
        console.log('✅ SignalR Chat reconnected. Connection ID:', connectionId)
      })

      this.chatConnection.onclose(error => {
        console.error('❌ SignalR Chat connection closed:', error)
      })

      await this.chatConnection.start()
      console.log('SignalR Chat Connected')
      if (this.isDebugEnabled()) this.attachDebugInterceptors()
      return this.chatConnection
    })()

    try {
      return await this.chatConnectionPromise
    } catch (err) {
      console.error('SignalR Chat Connection Error:', err)
      throw err
    } finally {
      this.chatConnectionPromise = null
    }
  }

  async stopChatConnection() {
    if (this.chatConnection) {
      await this.chatConnection.stop()
      this.chatConnection = null
      console.log('SignalR Chat Disconnected')
    }
  }

  getChatConnection() {
    return this.chatConnection
  }

  // Notification Hub
  async startNotificationConnection(token: string) {
    if (this.notificationConnection?.state === signalR.HubConnectionState.Connected) {
      console.log('✅ SignalR Notifications already connected')
      return this.notificationConnection
    }

    console.log('🔌 Attempting to connect to Notification Hub...')
    console.log('📍 Hub URL:', `${SIGNALR_URL}/hub/notifications`)

    this.notificationConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/notifications`, {
        accessTokenFactory: currentToken(token),
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.random() * 10000
          } else {
            return null
          }
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()

    // Add connection state handlers
    // Registered on the connection object, before it starts. SignalR keeps `.on` handlers across
    // its own automatic reconnects, so this is per-connection rather than per-connect.
    attachSettingsSync(this.notificationConnection)

    this.notificationConnection.onreconnecting(error => {
      console.warn('⚠️ SignalR Notifications reconnecting...', error)
    })

    this.notificationConnection.onreconnected(connectionId => {
      console.log('✅ SignalR Notifications reconnected. Connection ID:', connectionId)
      // A reconnect issues a NEW id. Leaving the old one in place would make the server think
      // every change from this tab came from a device that no longer exists, so the tab would
      // stop recognising its own echoes and reload its forms after each of its own saves.
      setRealtimeConnectionId(connectionId ?? null)
    })

    this.notificationConnection.onclose(error => {
      console.error('❌ SignalR Notifications connection closed:', error)
      setRealtimeConnectionId(null)
    })

    try {
      await this.notificationConnection.start()
      setRealtimeConnectionId(this.notificationConnection.connectionId ?? null)
      console.log('✅ SignalR Notifications Connected')
      console.log('📡 Connection ID:', this.notificationConnection.connectionId)
      console.log('🔊 Connection State:', this.notificationConnection.state)
      if (this.isDebugEnabled()) this.attachDebugInterceptors()
      return this.notificationConnection
    } catch (err) {
      console.error('❌ SignalR Notifications Connection Error:', err)
      throw err
    }
  }

  async stopNotificationConnection() {
    if (this.notificationConnection) {
      console.log('⚠️ Stopping notification connection...')
      await this.notificationConnection.stop()
      setRealtimeConnectionId(null)
      this.notificationConnection = null
      console.log('🔌 SignalR Notifications Disconnected')
    } else {
      console.log('ℹ️ No notification connection to stop')
    }
  }

  getNotificationConnection() {
    return this.notificationConnection
  }

  // Disconnect all
  async disconnectAll() {
    await this.stopChatConnection()
    await this.stopNotificationConnection()
  }

  // ========================
  // Debug Toggle
  // ========================
  private debugCleanups: Array<() => void> = []

  isDebugEnabled(): boolean {
    return localStorage.getItem('signalr-debug') === 'true'
  }

  enableDebug() {
    localStorage.setItem('signalr-debug', 'true')
    this.attachDebugInterceptors()
    console.log(
      '%c[SignalR Debug] ENABLED — all hub events will be logged. Run signalRDebug(false) to disable.',
      'color: #4caf50; font-weight: bold'
    )
  }

  disableDebug() {
    localStorage.removeItem('signalr-debug')
    this.detachDebugInterceptors()
    console.log(
      '%c[SignalR Debug] DISABLED',
      'color: #f44336; font-weight: bold'
    )
  }

  /** Patch active connections to log every server→client invocation */
  attachDebugInterceptors() {
    this.detachDebugInterceptors()

    const connections = [
      { name: 'Chat', conn: this.chatConnection },
      { name: 'Notifications', conn: this.notificationConnection },
      { name: 'Flow', conn: flowHubService.getConnection() },
    ]

    for (const { name, conn } of connections) {
      if (!conn) continue
      // Patch the internal _invokeClientMethod to intercept all events
      const original = (conn as any)._invokeClientMethod
      if (typeof original === 'function') {
        const patched = function (this: any, methodName: string, args: any[]) {
          const safeArgs = args || []
          console.groupCollapsed(
            `%c[SignalR ${name}]%c ${methodName}%c (${safeArgs.length} arg${safeArgs.length !== 1 ? 's' : ''})`,
            'color: #2196f3; font-weight: bold',
            'color: #ff9800; font-weight: bold',
            'color: #999; font-weight: normal'
          )
          safeArgs.forEach((arg: any, i: number) => {
            try {
              console.log(`%cArg ${i}:%c ${JSON.stringify(arg, null, 2)}`, 'color: #9c27b0; font-weight: bold', 'color: inherit')
            } catch {
              console.log(`Arg ${i}:`, arg)
            }
          })
          if (safeArgs.length > 0) {
            console.log('%cRaw:', 'color: #607d8b; font-weight: bold', ...safeArgs)
          }
          console.groupEnd()
          return original.call(this, methodName, args)
        };
        (conn as any)._invokeClientMethod = patched
        this.debugCleanups.push(() => {
          (conn as any)._invokeClientMethod = original
        })
      }
    }
  }

  detachDebugInterceptors() {
    this.debugCleanups.forEach(fn => fn())
    this.debugCleanups = []
  }
}

export const signalRService = new SignalRService()

// Expose global toggle: signalRDebug(true) / signalRDebug(false) / signalRDebug()
;(window as any).signalRDebug = (enabled?: boolean) => {
  if (enabled === undefined) {
    const state = signalRService.isDebugEnabled()
    console.log(`SignalR debug is ${state ? 'ON' : 'OFF'}. Usage: signalRDebug(true) or signalRDebug(false)`)
    return state
  }
  if (enabled) {
    signalRService.enableDebug()
  } else {
    signalRService.disableDebug()
  }
}
